/**
 * Client for the support-admin Phase 2 endpoints (ClickUp 869f0ev2j /
 * 869f0ev2r), implemented in apps/api-py by app/api/admin_credits.py and
 * app/api/admin_entitlements.py — see those files' module docstrings for the
 * reuse map (which existing product function each route calls).
 */

import { adminApiUrl } from './adminApi';

export interface LedgerRow {
  id: string;
  type: string;
  credits: number;
  source: string;
  metadata: Record<string, unknown> | null;
  date_created: string | null;
}

export interface OrganizationCredits {
  balance: number;
  monthly_limit: number | null;
  allowance_window: { start: string; end: string | null; source: string };
  ledger: LedgerRow[];
}

export interface GrantCreditsResult {
  ledger_row_id: string | null;
  replayed: boolean;
  amount: number;
  organization_id: string;
  platform: EntitlementPlatform | null;
}

/**
 * Which platform an entitlement WE grant is activated FOR (ClickUp 869f084w5
 * §3). Mirrors apps/api-py/app/models/entitlement_platform.py and migration
 * 0284's CHECK — widen all three or none.
 *
 * NOT a statement about which platform the USER is on. Measured on dev and
 * prod 16.09.2026: `profiles` has no platform column and every payment row is
 * `WEB`, so "does this person use App, Web or both" cannot be answered today.
 * `APP` exists here so that the day an App client ships, the record of what
 * support activated is unambiguous — including the rows written before it.
 */
export type EntitlementPlatform = 'WEB' | 'APP';

export const ENTITLEMENT_PLATFORMS: EntitlementPlatform[] = ['WEB', 'APP'];

export interface Plan {
  id: string;
  name: string;
  is_default: boolean;
  organization_count: number;
  features: Record<string, unknown> | null;
  limits: Record<string, unknown> | null;
}

export interface OrganizationBenefit {
  id: string;
  organization_id: string;
  key: string;
  value: Record<string, unknown>;
  valid_until: string | null;
  granted_by: string;
  reason: string;
  revoked_at: string | null;
  revoked_by: string | null;
  created_at: string;
  /** Which platform this entitlement was activated FOR (migration 0284).
   * 'WEB' for every row written before the column existed. */
  platform: EntitlementPlatform;
}

/** Body a caller could not have signed on their own is a bug the API returns
 * `{"error": string}` for — every helper below throws `Error(body.error)` on
 * a non-OK response so a form's catch block has one shape to render. */
async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `request failed: ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // non-JSON error body — keep the status-only message
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export async function fetchOrganizationCredits(orgId: string): Promise<OrganizationCredits> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/organizations/${orgId}/credits`), {
    credentials: 'include',
  });
  return asJson<OrganizationCredits>(res);
}

/**
 * `idempotencyKey` is required by the API (POST fails 400 without it) — the
 * caller mints one PER SUBMIT ATTEMPT (a fresh key on every button click,
 * the SAME key across the retries of one attempt), so a network retry of the
 * same click replays instead of double-granting, while two separate clicks
 * on the same form are two distinct grants.
 */
export async function grantCredits(
  orgId: string,
  /**
   * `reason` is OPTIONAL here and nowhere else in this file (Rostyslav
   * 22.09.2026, owner-approved) — every other helper below still takes a
   * required one, because plan activation/revert, benefit grant/revoke and the
   * run refund were not asked about and are not this change.
   *
   * The audit row is written either way: with no reason the API records the
   * explicit `(no reason given)` marker plus `reason_given: false`, never a
   * blank — see apps/api-py/app/api/admin_credits.py:NO_REASON_GIVEN.
   */
  input: { amount: number; reason?: string; note?: string; platform?: EntitlementPlatform },
  idempotencyKey: string
): Promise<GrantCreditsResult> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/organizations/${orgId}/credits/grant`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(input),
  });
  return asJson<GrantCreditsResult>(res);
}

export async function refundRun(
  orgId: string,
  runId: string,
  reason: string
): Promise<{ credits_refunded: number }> {
  const res = await fetch(
    adminApiUrl(`/api/admin/v2/organizations/${orgId}/credits/refund-run/${runId}`),
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  );
  return asJson<{ credits_refunded: number }>(res);
}

export async function fetchPlans(): Promise<Plan[]> {
  const res = await fetch(adminApiUrl('/api/admin/v2/plans'), { credentials: 'include' });
  const body = await asJson<{ plans: Plan[] }>(res);
  return body.plans;
}

export async function activatePlan(
  orgId: string,
  input: {
    plan_id: string;
    valid_until: string | null;
    reason: string;
    platform?: EntitlementPlatform;
  }
): Promise<{
  plan_id: string;
  previous_plan_id: string | null;
  benefit_id: string | null;
  platform: EntitlementPlatform | null;
}> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/organizations/${orgId}/plan`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return asJson(res);
}

export async function revertPlan(
  orgId: string,
  reason: string
): Promise<{ plan_id: string | null }> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/organizations/${orgId}/plan`), {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return asJson(res);
}

export async function fetchBenefits(orgId: string): Promise<OrganizationBenefit[]> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/organizations/${orgId}/benefits`), {
    credentials: 'include',
  });
  const body = await asJson<{ benefits: OrganizationBenefit[] }>(res);
  return body.benefits;
}

export async function grantBenefit(
  orgId: string,
  input: {
    benefit_key: string;
    valid_until: string;
    reason: string;
    platform?: EntitlementPlatform;
  }
): Promise<{
  benefit_id: string;
  key: string;
  valid_until: string;
  platform: EntitlementPlatform | null;
}> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/organizations/${orgId}/benefits`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return asJson(res);
}

export async function revokeBenefit(
  orgId: string,
  benefitId: string,
  reason: string
): Promise<{ key: string; reverted_to: unknown }> {
  const res = await fetch(
    adminApiUrl(`/api/admin/v2/organizations/${orgId}/benefits/${benefitId}`),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  );
  return asJson(res);
}

/**
 * One-time passes — ClickUp 869f084w5 §3's "manual activation of subscriptions
 * and ONE-TIME PASSES with a defined validity period". On this platform a
 * one-time pass is the Booster: a credit-multiplier window
 * (`org_credit_multipliers`) that multiplies what the organization BUYS while
 * it is open. The other `onetime` product payment-back sells is the credits
 * pack, which is already reachable as a plain credit grant.
 */

export interface OneTimePassWindow {
  multiplier: number | null;
  active_from?: string | null;
  active_until: string | null;
  source: string | null;
  provider?: string | null;
  sku?: string | null;
}

export interface OneTimePassResult {
  multiplier: number;
  days: number;
  platform: EntitlementPlatform | null;
  active_until: string | null;
  replayed: boolean;
}

export async function fetchOneTimePass(orgId: string): Promise<OneTimePassWindow> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/organizations/${orgId}/one-time-pass`), {
    credentials: 'include',
  });
  return asJson<OneTimePassWindow>(res);
}

export async function activateOneTimePass(
  orgId: string,
  input: {
    multiplier: number;
    days: number;
    reason: string;
    platform?: EntitlementPlatform;
  },
  idempotencyKey: string
): Promise<OneTimePassResult> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/organizations/${orgId}/one-time-pass`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(input),
  });
  return asJson<OneTimePassResult>(res);
}

export async function endOneTimePass(
  orgId: string,
  reason: string
): Promise<{ ok: boolean; ended_activation_ref: string }> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/organizations/${orgId}/one-time-pass`), {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return asJson(res);
}

/**
 * The coin-refund calculator (ClickUp 869f7jkj5) — apps/api-py
 * app/api/admin_refund_calculator.py. GET prefills the screen; POST cuts the
 * coins. The money itself is NOT moved by either: `next_step` names where the
 * agent issues it.
 */

export interface RefundablePurchase {
  ledger_row_id: string;
  coins: number;
  /** Provider minor units (cents for USD); null when the row did not say. */
  price_cents: number | null;
  currency: string | null;
  provider: string | null;
  provider_order_id: string | null;
  sku: string | null;
  date_created: string | null;
  /** The share-refund this console already took for this purchase. */
  clawback: {
    ledger_row_id: string;
    coins_deducted: number;
    percent: number | null;
    refund_cents: number | null;
    decided_by: string | null;
    date_created: string | null;
  } | null;
  /** Coins a provider refund webhook already reversed for this order. */
  reversed_by_provider: number;
}

export interface RefundCalculatorData {
  organization_id: string;
  organization_name: string | null;
  balance: { remaining: number | null; unlimited: boolean; wallet: number };
  purchases: RefundablePurchase[];
  default_percent: number;
  min_percent: number;
  max_percent: number;
}

export interface RefundNextStep {
  kind: 'refund_order' | 'provider_partial_refund';
  amount_cents: number;
  currency: string | null;
  provider_order_id: string | null;
}

export interface RefundClawbackResult {
  ledger_row_id: string;
  replayed: boolean;
  quote: {
    percent: number;
    refund_cents: number;
    coins_to_cut: number;
    coins_deducted: number;
    balance_before: number;
    balance_after: number;
    shortfall: number;
    fair_percent: number;
  } | null;
  next_step: RefundNextStep | null;
}

export async function fetchRefundCalculator(orgId: string): Promise<RefundCalculatorData> {
  const res = await fetch(adminApiUrl(`/api/admin/v2/organizations/${orgId}/refund-calculator`), {
    credentials: 'include',
  });
  return asJson<RefundCalculatorData>(res);
}

/** Same Idempotency-Key discipline as `grantCredits`: one key per confirm
 * ATTEMPT, so a double click or a network retry replays instead of cutting
 * twice. */
export async function refundClawback(
  orgId: string,
  input: {
    price_cents: number;
    coins_purchased: number;
    percent: number;
    reason: string;
    entered_balance: number;
    purchase_ledger_row_id?: string;
    currency?: string;
  },
  idempotencyKey: string
): Promise<RefundClawbackResult> {
  const res = await fetch(
    adminApiUrl(`/api/admin/v2/organizations/${orgId}/credits/refund-clawback`),
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(input),
    }
  );
  return asJson<RefundClawbackResult>(res);
}
